import { tz } from "moment-timezone";
import moment, { Moment } from "moment";
import {
  CountryISO2,
  DATE_FORMAT,
  IPickupLocation,
  IValidatedAddressV2,
} from "../types";
import { find } from "geo-tz";
import { countryNameToISO2 } from "./LabelHelpers";

const HOLIDAY_FORMAT = "YYYY-MM-DD";

// List of Holidays, reference: https://docs.google.com/spreadsheets/d/1DTRz12S7MyB6Ot7CR7x6On7APIeoGwltFqgnbeD44zY/edit#gid=0
export const CanadaHolidays = new Set([
  "2021-10-11", // Thanksgiving Day
  "2021-12-25", // Christmas Day
  "2021-12-26", // Boxing Day
  "2022-01-01", // New Year's Day
  "2022-02-21", // Family Day
  "2022-04-15", // Good Friday
  "2022-05-23", // Victoria Day
  "2022-07-01", // Canada Day
  "2022-08-01", // Civic Day
  "2022-09-05", // Labour Day
  "2022-10-10", // Thanksgiving
  "2022-12-26", // Christmas Day
  "2023-01-02", // New Years
  "2023-05-22", // Victoria Day
  "2023-07-01", // Canada Day
  "2023-09-04", // Labour Day
  "2023-10-09", // Thanksgiving
  "2023-12-25", // Christmas Day
  "2024-01-01", // New Years Day
]);
export const USHolidays = new Set([
  "2021-11-25", // Thanksgiving Day
  "2021-12-25", // Christmas Day
  "2021-12-31", // New Year's Eve
  "2022-01-01", // New Year's Day
  "2022-01-17", // Martin Luther King Jr. Day
  "2022-02-21", // Presidents' Day
  "2022-05-30", // Memorial Day
  "2022-09-05", // Labor Day
  "2022-11-24", // Thanksgiving Day
  "2022-12-26", // Christmas Day
  "2023-01-02", // New Years day
  "2023-05-29", // Memorial Day
  "2023-06-19", // Juneteenth
  "2023-07-04", // 4th of July
  "2023-09-04", // Labor Day
  "2023-11-23", // Thanksgiving Day
  "2023-12-25", // Christmas Day
  "2024-01-01", // New Years Day
]);

/**
 * Generates earliest possible ship date given cut off time.
 * @param pickupLocation merchant pickup location
 * @param timezone local timezone (ex: America/Toronto) optional
 */
export const getEarliestShipDate = (
  pickupLocation: IPickupLocation,
  timezone?: string
): Moment => {
  const {
    cutoffTime,
    processingDays,
    eligibleShipDays,
    contact: {
      address: { country },
    },
  } = pickupLocation;

  const currentTime = timezone ? tz(timezone) : moment();

  const merchantCutoffTime = timezone
    ? tz(cutoffTime, DATE_FORMAT.CUT_OFF_TIME, timezone).date(
        currentTime.date()
      )
    : moment(cutoffTime, DATE_FORMAT.CUT_OFF_TIME).date(currentTime.date());

  return getEarliestShipDateRecursive(
    currentTime,
    merchantCutoffTime,
    processingDays,
    eligibleShipDays,
    country,
  );
};

export const getEarliestShipDateRecursive = (
  currentTime: Moment,
  merchantCutoffTime: Moment,
  processingDays: number,
  eligibleShipDays: number[],
  country: string,
  requestedTime = currentTime,
): Moment => {
  if (currentTime.isAfter(merchantCutoffTime, "day")) {
    throw new Error(
      "currentTime cannot be the day after the merchantCutoffTime."
    );
  }

  // Eligible day check
  const currentDay = currentTime.day();
  const isEligibleShipDay = eligibleShipDays.includes(currentDay);

  // Holiday Check
  const holidays =
    countryNameToISO2(country) === CountryISO2.CANADA
      ? CanadaHolidays
      : USHolidays;
  const isHoliday = holidays.has(currentTime.format(HOLIDAY_FORMAT));

  const startOfNextDay = currentTime.clone().add("1", "days").startOf("day");

  const nextCutoffTime = merchantCutoffTime.clone().add("1", "days");

  if (isEligibleShipDay && !isHoliday) {
    // Cutoff Time
    if (currentTime.isBefore(merchantCutoffTime)
    || (currentTime.isSame(merchantCutoffTime) && currentTime !== requestedTime)) {
      // Processing Time
      if (processingDays === 0) {
        return currentTime.clone().startOf("day");
      } else {
        return getEarliestShipDateRecursive(
          startOfNextDay, // Increment day by one
          nextCutoffTime,
          processingDays - 1, // Decrease processing days by 1
          eligibleShipDays,
          country,
          requestedTime,
        );
      }
    } else {
      return getEarliestShipDateRecursive(
        startOfNextDay,
        nextCutoffTime,
        processingDays,
        eligibleShipDays,
        country,
        requestedTime,
      );
    }
  } else {
    return getEarliestShipDateRecursive(
      startOfNextDay, // Increment day by one
      nextCutoffTime,
      processingDays,
      eligibleShipDays,
      country,
      requestedTime,
    );
  }
};

/**
 * Generates next business day given package destination
 * if no destination is provided, gets the next business day based on server timezone and Canadian holidays
 * @param destination package destination address
 */
export const getNextBusinessDay = (
  destination?: IValidatedAddressV2
): Moment => {
  let currentTime = moment();
  if (destination) {
    const timezone = find(
      destination.geocode?.latitude as number,
      destination.geocode?.longitude as number
    )[0];
    currentTime = tz(timezone);
  }

  const holidays =
    destination?.country === CountryISO2.USA ? USHolidays : CanadaHolidays;
  let nextDayTime = currentTime.clone().add("1", "days");

  // pass over the weekend days and holidays
  while (
    [0, 6].includes(nextDayTime.day()) ||
    holidays.has(nextDayTime.format(HOLIDAY_FORMAT))
  ) {
    nextDayTime = nextDayTime.clone().add("1", "days");
  }

  return nextDayTime.clone().startOf("day");
};
